import { Component, Input, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ModelViewerComponent } from '../../components/model-viewer/model-viewer.component'; // Kendi ModelViewerComponent yolunuzu ekleyin
import { SelectComponent } from 'app/shared/components/select/select.component';
import { WoundService } from 'app/core/services/wound/wound.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ScalesModalComponent } from 'app/shared/components/scales-modal/scales-modal.component';
import { FullscreenService } from 'app/core/services/fullscreen.service';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { GetWoundByIdResponse, WoundAnalysisMetrics } from 'app/core/models/wound/wound.model';

@Component({
  selector: 'app-model-viewer-page',
  standalone: true,
  imports: [
    CommonModule,
    DecimalPipe,
    FormsModule,
    ModelViewerComponent,
    SelectComponent,
    TranslateModule,
  ],
  templateUrl: './model-viewer-page.component.html',
  styleUrls: ['./model-viewer-page.component.scss'],
})
export class ModelViewerPageComponent implements OnInit, OnDestroy {
  @Input() woundIds: any;
  @Input() showScalesControl: boolean = false;
  @Input() woundDateMap: { [id: string]: Date } = {};
  @Input() activeTab: string = 'models';
  @Input() set wound(value: GetWoundByIdResponse | null) {
    this._wound = value;
    this.fetchMetrics();
  }
  get wound(): GetWoundByIdResponse | null { return this._wound; }

  private _wound: GetWoundByIdResponse | null = null;
  viewer1Metrics: WoundAnalysisMetrics | null = null;

  get viewer1BoundingBoxLabel(): string {
    const bb = this.viewer1Metrics?.boundingBoxMm;
    if (!bb || bb.length < 3) return '—';
    const c = (mm: number) => (mm / 10).toFixed(2);
    return `${c(bb[0])}×${c(bb[1])}×${c(bb[2])} cm`;
  }

  get hasSteepestViewImages(): boolean {
    return !!(this.viewer1Metrics?.steepestViewOriginalPath && this.viewer1Metrics?.steepestViewSegmentedPath);
  }

  get showViewerMetricsStrip(): boolean {
    return this.activeTab === 'metrics' || this.activeTab === 'analysis';
  }

  readonly mockTissues: { key: string; percent: number; color: string }[] = [
    { key: 'granulation', percent: 45, color: '#e74c3c' },
    { key: 'muscle',      percent: 30, color: '#e67e22' },
    { key: 'necrotic',    percent:  8, color: '#2c3e50' },
    { key: 'slough',      percent:  7, color: '#f39c12' },
    { key: 'bone',        percent:  5, color: '#ecf0f1' },
    { key: 'tendon',      percent:  5, color: '#9b59b6' },
  ];

  get mockGranulationPercent(): number {
    return this.mockTissues.find(t => t.key === 'granulation')?.percent ?? 0;
  }

  get pieSegments(): { key: string; percent: number; color: string; dashoffset: number }[] {
    let cumulative = 0;
    return this.mockTissues.map(t => {
      const dashoffset = 25 - cumulative;
      cumulative += t.percent;
      return { ...t, dashoffset };
    });
  }

  selectedViewerForScales: 'viewer1' | 'viewer2' | null = null;

  models: {
    id: string;
    name: string;
    url: string;
    woundId: string;
    notes: any[];
    is3D?: boolean;
  }[] = [];

  modelsList1: any[] = [];
  modelsList2: any[] = [];

  selectedModel1: string | null = null;
  selectedModel2: string | null = null;

  selectedModel1Notes: any;
  selectedModel2Notes: any;

  viewer1WoundId: string | null = null;
  viewer2WoundId: string | null = null;
  isLoading: boolean = false;

  @ViewChild('modelsGrid', { read: ElementRef }) modelsGridRef!: ElementRef<HTMLElement>;
  @ViewChild('viewerCard1', { read: ElementRef }) viewerCard1Ref!: ElementRef<HTMLElement>;
  @ViewChild('viewerCard2', { read: ElementRef }) viewerCard2Ref!: ElementRef<HTMLElement>;
  
  isSplitScreenFullscreen: boolean = false;
  isViewer1Fullscreen: boolean = false;
  isViewer2Fullscreen: boolean = false;
  
  private fullscreenSubscription?: Subscription;

  constructor(
    private woundService: WoundService,
    private modalService: NgbModal,
    private fullscreenService: FullscreenService,
    private http: HttpClient,
  ) {}

  private fetchMetrics(): void {
    if (!this._wound?.metricsPath) {
      this.viewer1Metrics = null;
      return;
    }
    this.http.get<Record<string, unknown>>(this._wound.metricsPath).subscribe({
      next: (data) => {
        const raw = data as any;
        this.viewer1Metrics = {
          surfaceAreaMm2: raw.surface_area_mm2 ?? raw.surfaceAreaMm2 ?? 0,
          surfaceAreaCm2: raw.surface_area_cm2 ?? raw.surfaceAreaCm2 ?? 0,
          volumeMm3: raw.volume_mm3 ?? raw.volumeMm3 ?? 0,
          volumeCm3: raw.volume_cm3 ?? raw.volumeCm3 ?? 0,
          volumeIsApproximate: raw.volume_is_approximate ?? raw.volumeIsApproximate ?? false,
          isWatertight: raw.is_watertight ?? raw.isWatertight ?? false,
          boundingBoxMm: raw.bounding_box_mm ?? raw.boundingBoxMm,
          centroidMm: raw.centroid_mm ?? raw.centroidMm,
          boundaryLengthMm: raw.boundary_length_mm ?? raw.boundaryLengthMm ?? 0,
          numVertices: raw.num_vertices ?? raw.numVertices ?? 0,
          numFaces: raw.num_faces ?? raw.numFaces ?? 0,
          steepestViewOriginalPath:
            raw.steepest_view_original_path ?? raw.steepestViewOriginalPath ?? null,
          steepestViewSegmentedPath:
            raw.steepest_view_segmented_path ?? raw.steepestViewSegmentedPath ?? null,
        };
      },
      error: () => { this.viewer1Metrics = null; },
    });
  }

  ngOnInit(): void {
    this.loadModels();
    
    this.fullscreenSubscription = this.fullscreenService.currentFullscreenElement$.subscribe(
      (element) => {
        this.isSplitScreenFullscreen = element === this.modelsGridRef?.nativeElement;
        
        this.isViewer1Fullscreen = element === this.viewerCard1Ref?.nativeElement;
        
        this.isViewer2Fullscreen = element === this.viewerCard2Ref?.nativeElement;
      }
    );
  }

  ngOnDestroy(): void {
    this.fullscreenSubscription?.unsubscribe();
  }

  handleMarkerCreation(event: any) {
    this.loadModels();
  }

  handleMarkerDeletion(event: any) {
    this.loadModels();
  }

  handleMarkerUpdated(event: any) {
    this.loadModels();
  }

  onModelChange() {
    this.updateSelectedNotes();
  }

  private loadModels(): void {
    if (!this.woundIds || this.woundIds.length === 0) return;

    this.isLoading = true;

    // Set default wound IDs based on input array (Last vs Last-1)
    const currentWoundId = this.woundIds[this.woundIds.length - 1];
    const previousWoundId =
      this.woundIds.length > 1 ? this.woundIds[this.woundIds.length - 2] : null;

    this.viewer1WoundId = currentWoundId;
    this.viewer2WoundId = previousWoundId;

    const capturedSelection1 = this.selectedModel1;
    const capturedSelection2 = this.selectedModel2;

    this.woundService.getWoundModelNotesByWoundIds(this.woundIds).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (!res?.data || res.data.length === 0) {
          this.models = [];
          this.modelsList1 = [];
          this.modelsList2 = [];
          this.selectedModel1 = null;
          this.selectedModel2 = null;
          this.updateSelectedNotes();
          return;
        }
        const grouped = res.data.reduce(
          (acc, item) => {
            const key = `${item.fileName}_${item.woundId}`;
            if (!acc[key]) {
              acc[key] = {
                fileName: item.fileName,
                woundId: item.woundId,
                notes: [],
              };
            }
            acc[key].notes.push(...item.notes);
            return acc;
          },
          {} as Record<string, any>,
        );

        const uniqueWoundIds = [
          ...new Set(res.data.map((item) => item.woundId)),
        ];

        this.models = Object.values(grouped).map(
          (group: any, index: number) => {
            const lowerUrl = group.fileName.toLowerCase();
            const is3D =
              lowerUrl.endsWith('.gltf') || lowerUrl.endsWith('.glb');

            return {
              id: `${group.fileName}_${group.woundId}`,
              name: '',
              url: group.fileName.startsWith('src/')
                ? group.fileName.substring(3)
                : group.fileName,
              woundId: group.woundId,
              notes: group.notes,
              is3D: is3D,
            };
          },
        );

        // Sort models by woundIds inputs order
        if (this.woundIds && this.woundIds.length > 0) {
          this.models.sort((a, b) => {
            return (
              this.woundIds.indexOf(a.woundId) -
              this.woundIds.indexOf(b.woundId)
            );
          });
        }

        const woundModelCounters: {
          [woundId: string]: { image: number; scan: number };
        } = {};

        this.models.forEach((model) => {
          if (!woundModelCounters[model.woundId]) {
            woundModelCounters[model.woundId] = { image: 0, scan: 0 };
          }

          if (model.is3D) {
            woundModelCounters[model.woundId].scan++;
            model.name = `Scan-${woundModelCounters[model.woundId].scan}`;
          } else {
            woundModelCounters[model.woundId].image++;
            model.name = `Image-${woundModelCounters[model.woundId].image}`;
          }
        });

        if (this.models.length > 0) {
          const currentWoundId = this.woundIds[this.woundIds.length - 1]; // Last ID is current
          // Previous is second to last if it exists and is different from current
          const previousWoundId =
            this.woundIds.length > 1
              ? this.woundIds[this.woundIds.length - 2]
              : null;

          this.viewer1WoundId = currentWoundId;
          this.viewer2WoundId = previousWoundId;

          this.modelsList1 = this.models.filter(
            (m) => m.woundId === currentWoundId,
          );
          this.modelsList2 = previousWoundId
            ? this.models.filter((m) => m.woundId === previousWoundId)
            : [];

          if (this.modelsList1.length > 0) {
            const stillExists =
              capturedSelection1 &&
              this.modelsList1.find((m) => m.id === capturedSelection1);

            if (stillExists) {
              this.selectedModel1 = capturedSelection1;
            } else {
              this.selectedModel1 =
                this.modelsList1[this.modelsList1.length - 1].id;
            }
          } else {
            this.selectedModel1 = null;
          }

          if (this.modelsList2.length > 0) {
            const stillExists =
              capturedSelection2 &&
              this.modelsList2.find((m) => m.id === capturedSelection2);

            if (stillExists) {
              this.selectedModel2 = capturedSelection2;
            } else {
              this.selectedModel2 =
                this.modelsList2[this.modelsList2.length - 1].id;
            }
          } else {
            this.selectedModel2 = null;
          }

          this.updateSelectedNotes();
        }
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private updateSelectedNotes(): void {
    this.selectedModel1Notes = this.models.find(
      (m) => m.id === this.selectedModel1,
    );
    this.selectedModel2Notes = this.models.find(
      (m) => m.id === this.selectedModel2,
    );
  }

  toggleScaleSelection(viewer: 'viewer1' | 'viewer2'): void {
    if (this.selectedViewerForScales === viewer) {
      this.selectedViewerForScales = null;
    } else {
      this.selectedViewerForScales = viewer;
    }
  }

  selectWound(woundId: string) {
    if (!this.selectedViewerForScales) return;

    // Filter models for this specific wound
    const modelsForWound = this.models.filter((m) => m.woundId === woundId);

    if (this.selectedViewerForScales === 'viewer1') {
      this.viewer1WoundId = woundId;
      this.modelsList1 = modelsForWound;
      if (this.modelsList1.length > 0) {
        this.selectedModel1 = this.modelsList1[this.modelsList1.length - 1].id;
      } else {
        this.selectedModel1 = null;
      }
    } else if (this.selectedViewerForScales === 'viewer2') {
      this.viewer2WoundId = woundId;
      this.modelsList2 = modelsForWound;
      if (this.modelsList2.length > 0) {
        this.selectedModel2 = this.modelsList2[this.modelsList2.length - 1].id;
      } else {
        this.selectedModel2 = null;
      }
    }

    this.updateSelectedNotes();
  }

  openScalesModal(viewer: 'viewer1' | 'viewer2'): void {
    const woundId =
      viewer === 'viewer1' ? this.viewer1WoundId : this.viewer2WoundId;

    if (!woundId) return;

    const modalRef = this.modalService.open(ScalesModalComponent, {
      size: 'xl',
      centered: true,
    });
    modalRef.componentInstance.woundId = woundId;
  }

  toggleFullscreen(element: HTMLElement) {
    this.fullscreenService.toggle(element);
  }
}
